import React, { useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

import { useStorage } from "@smart/itops-hooks-dom";

import { getTabClassName, getTabLabelClassName } from "./class-names";
import { Button } from "../button";
import { IconName } from "../icon";

export type Tab = {
  key: string;
  label: string;
  component: React.ReactNode;
  icon?: IconName;
  iconClassName?: string;
  activeIcon?: IconName;
};

export type TabsProps = {
  tabs: Tab[];
  activeTab?: string;
  onTabChange?: (tabKey: string) => void;
  className?: string;
};

export const Tabs = ({
  tabs,
  activeTab,
  onTabChange,
  className,
}: TabsProps) => {
  const tabSchema = z.string();

  const [currentTab, setCurrentTab] = useStorage({
    defaultValue: tabs[0].key,
    key: "home-tab",
    schema: tabSchema,
    storage: "local",
  });

  const handleTabChange = (tabKey: string) => {
    if (tabKey === currentTab) return;

    setCurrentTab(tabKey);
    if (onTabChange) onTabChange(tabKey);
  };

  useEffect(() => {
    if (activeTab) {
      setCurrentTab(activeTab);
    }
  }, [activeTab]);

  return (
    <div
      className={twMerge(
        "flex flex-col overflow-hidden w-full bg-white rounded text-ui pt-4",
        className,
      )}
    >
      <div className="flex gap-8 px-8 border-b border-neutral-200 relative">
        {tabs.map((tab) => {
          const activeIcon = tab.activeIcon || tab.icon;
          const isActive = currentTab === tab.key;
          return (
            <div
              key={tab.key}
              role="button"
              tabIndex={0}
              className={getTabClassName({ active: isActive })}
              onClick={() => handleTabChange(tab.key)}
              onKeyDown={() => handleTabChange(tab.key)}
            >
              <Button
                leftIcon={isActive ? activeIcon : tab.icon}
                variant="inlineSubtle"
                className={twMerge(
                  "focus:before:border-none",
                  tab.iconClassName,
                )}
              />
              <div
                className={getTabLabelClassName({
                  active: currentTab === tab.key,
                })}
                aria-selected={isActive}
              >
                {tab.label}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col mt-4 overflow-auto flex-1">
        {tabs.find((tab) => tab.key === currentTab)?.component}
      </div>
    </div>
  );
};
